import type { Navigator, Screen } from "../app/screen";
import { Api, ApiError } from "../net/api";
import { linkWalletFlow } from "../net/linkWallet";
import { pageShell, note } from "../ui/page";
import { loadCardNames, cardName } from "../match/cards";
import { settingsButton } from "../ui/settings";

/**
 * PROFILE - your record.
 *
 * ⭐ **THE WALLET IS THE IDENTITY, AND THIS SCREEN DOES NOT INVENT A SECOND ONE.**
 * `../VendettaSaloon/docs/PLATFORM-PROFILE-SPEC.md` makes the wallet-keyed `PlayerAccount` the one
 * owner of identity across Vendetta, and `LoginGuest` derives the player from the beta gate cookie
 * "never from anything the client sends". So this screen READS; it does not mint a name, a level or
 * a record of its own.
 *
 * ⚠️ **A ZERO IS A FACT AND MUST LOOK LIKE ONE.** A new player has 0 games, 0 wins and an unranked
 * elo, and a screen that hides those behind "-" or an empty panel reads as broken. Zeros are shown,
 * with the win rate explicitly marked as not yet meaningful rather than rendered as 0%.
 *
 * ⚠️ **`leagueStats` IS KEYED BY LEAGUE ID**, so the league NAMES come from `AvailableLeagues` -
 * never from a list here. Alex is expanding the ladder; a hard-coded league name would be wrong the
 * day one is added, and `verify_open_sets.py` exists because that class of list keeps reappearing.
 */
export const ProfileScreen: Screen = {
  id: "profile",

  mustOffer: ["BACK"],

  async mount(host: HTMLElement, nav: Navigator): Promise<void> {
    const shell = pageShell(nav, "PROFILE", "your record");
    host.append(shell.el);

    const root = document.createElement("div");
    root.className = "profile";
    shell.body.append(root);
    root.append(note("Loading your record..."));

    void loadCardNames();

    let stats: Record<string, unknown> = {};
    let user: Record<string, unknown> = {};
    let leagues: Array<{ id?: string; leagueName?: string }> = [];

    try {
      [stats, user, leagues] = await Promise.all([
        Api.playerStatistics() as Promise<Record<string, unknown>>,
        Api.userData() as Promise<Record<string, unknown>>,
        Api.leagues() as Promise<Array<{ id?: string; leagueName?: string }>>,
      ]);
    } catch (e) {
      root.replaceChildren(note(`Could not load your record: ${describe(e)}`, true));
      return;
    }

    const games = num(stats["games"]);
    const wins = num(stats["wins"]);
    const losses = num(stats["losses"]);
    const seconds = num(stats["timePlayedInSeconds"]);

    root.replaceChildren();

    // ── who ──────────────────────────────────────────────────────────────────────────────────────
    const who = document.createElement("section");
    who.className = "profile__who";

    const name = document.createElement("h2");
    name.className = "profile__name";
    // ⚠️ textContent, and the wallet is the fallback - never "Player" or "Guest", which would be a
    // name this screen invented.
    name.textContent = str(user["userName"]) || shorten(str(user["primaryWalletAddress"]))
      || "Unnamed";

    // ⭐ **THE PLAYER CHOOSES THEIR OWN NAME.** Alex, 2026-08-15: *"no way to edit my name in the
    // profile page."* Until now everyone wore `PlayerNames.For(userId)` - a derived two-word name
    // they never picked and could not change.
    const rename = document.createElement("button");
    rename.type = "button";
    rename.className = "utilbtn profile__rename";
    rename.dataset["label"] = "EDIT NAME";
    rename.textContent = "EDIT NAME";

    // ⭐ **REROLL - A NEW NAME WITHOUT HAVING TO THINK OF ONE.** Alex, 2026-08-15: *"we talked about
    // having the feature to reroll your name for a new random name."* Naming yourself is a blank
    // page, and the generated names are the only place most players meet the setting's vocabulary.
    const reroll = document.createElement("button");
    reroll.type = "button";
    reroll.className = "utilbtn profile__reroll";
    reroll.dataset["label"] = "REROLL";
    reroll.textContent = "REROLL";
    reroll.title = "Draw another name from the Longwake";

    const nameRow = document.createElement("div");
    nameRow.className = "profile__namerow";
    nameRow.append(name, rename, reroll);

    const wallet = document.createElement("p");
    wallet.className = "profile__wallet";
    const addr = str(user["primaryWalletAddress"]);
    wallet.textContent = addr ? shorten(addr) : "No wallet linked";

    // ⭐ **CONNECT A WALLET - AS IDENTITY, NOT AS A DOOR.** Alex: *"we need to have the wallet gate
    // so we can track accounts. It is like the saloon where you sign in with your wallet."*
    //
    // ⛔ **IT LINKS; IT DOES NOT SIGN YOU IN AS SOMEBODY ELSE.** The beta is deliberately OPEN, and a
    // guest's decks, stars and won cards are keyed to their guest id. Switching identity on connect
    // would hand them an empty account and read as the game losing their save.
    const connect = document.createElement("button");
    connect.type = "button";
    connect.className = "utilbtn profile__connect";
    connect.dataset["label"] = "CONNECT WALLET";
    connect.textContent = addr ? "CHANGE WALLET" : "CONNECT WALLET";

    const walletRow = document.createElement("div");
    walletRow.className = "profile__namerow";
    walletRow.append(wallet, connect);

    const say = document.createElement("p");
    say.className = "profile__note";
    say.hidden = true;

    who.append(nameRow, walletRow, say);

    function saySo(text: string, bad = false): void {
      say.textContent = text;
      say.hidden = false;
      say.classList.toggle("profile__note--error", bad);
    }

    rename.addEventListener("click", () => {
      // ⚠️ `prompt` is deliberate here and not laziness: it is modal, it is keyboard-native, and it
      // cannot be left in a half-edited state on a screen the player navigates away from. A bespoke
      // inline editor is a bigger surface than the feature.
      const next = window.prompt("What should we call you?", name.textContent ?? "")?.trim();
      if (!next || next === name.textContent) return;
      rename.disabled = true;
      void Api.setUserName(next)
        .then((r) => { name.textContent = r.userName; saySo(`You are ${r.userName}.`); })
        // ⚠️ The SERVER'S words: it owns the length and character rules and states them precisely.
        .catch((e) => saySo(e instanceof ApiError ? (e.body || e.message) : String(e), true))
        .finally(() => { rename.disabled = false; });
    });

    reroll.addEventListener("click", () => {
      reroll.disabled = true;
      void Api.rerollName()
        .then((r) => { name.textContent = r.userName; saySo(`You are ${r.userName}.`); })
        .catch((e) => saySo(e instanceof ApiError ? (e.body || e.message) : String(e), true))
        .finally(() => { reroll.disabled = false; });
    });

    connect.addEventListener("click", () => {
      void connectWallet();
    });

    // ⭐ **THE ADDRESS IS SHOWN SHORT AND COPIES LONG.** Alex, 2026-08-15: *"make the wallet address
    // section copyable, and when you copy and paste it, it is the full address."*
    //
    // 🚨 **A TRUNCATED ADDRESS THAT COPIES AS TRUNCATED IS WORSE THAN NO COPY BUTTON.** `0xa2be…1499`
    // pasted into a block explorer, a whitelist or a support message is not a valid address, and it
    // fails LATER - at the point of use, by which time nobody suspects the copy. The ellipsis is
    // presentation; the clipboard must always carry the full 42 characters.
    if (addr) {
      wallet.classList.add("profile__wallet--copy");
      wallet.tabIndex = 0;
      wallet.setAttribute("role", "button");
      // ⚠️ The full address goes in the accessible name and the tooltip, so it is readable and
      // selectable even where the clipboard API is unavailable (insecure origin, denied permission).
      wallet.title = addr;
      wallet.setAttribute("aria-label", `Copy wallet address ${addr}`);

      const copyAddress = (): void => {
        void (async () => {
          try {
            // `navigator.clipboard` needs a secure context; the fallback is not decoration, it is
            // what makes this work at all over plain http on the box's IP.
            if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(addr);
            else legacyCopy(addr);
            saySo("Full address copied.");
          } catch {
            saySo("Could not reach the clipboard. The full address is in the tooltip.", true);
          }
        })();
      };

      wallet.addEventListener("click", copyAddress);
      wallet.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") { e.preventDefault(); copyAddress(); }
      });
    }

    async function connectWallet(): Promise<void> {
      // ⭐ THE FLOW ITSELF LIVES IN `net/linkWallet.ts`, because the victory screen now needs it too
      // (Alex: *"an unlinked player should get prompted to connect a wallet to get their reward"*).
      // This function owns the SCREEN's reaction to the outcome; it does not own the handshake.
      connect.disabled = true;
      try {
        const r = await linkWalletFlow();
        if (r.ok) {
          wallet.textContent = shorten(r.address);
          connect.textContent = "CHANGE WALLET";
          saySo("Wallet linked. Your progress stays on this account.");
        } else {
          saySo(r.reason, true);
        }
      } finally {
        connect.disabled = false;
      }
    }

    // ── the record ───────────────────────────────────────────────────────────────────────────────
    const grid = document.createElement("div");
    grid.className = "profile__stats";
    grid.append(
      figure("GAMES", String(games)),
      figure("WINS", String(wins)),
      figure("LOSSES", String(losses)),
      // ⚠️ Not "0%": with no games played a win rate is undefined, and 0% says something false.
      figure("WIN RATE", games > 0 ? `${Math.round((wins / games) * 100)}%` : "—",
             games > 0 ? undefined : "no games played yet"),
      // ⛔ **THE EARNED BALANCE IS NOT SHOWN HERE, AND THAT IS DELIBERATE.**
      //
      // Alex settled the design 2026-08-15: Revival's earned currency IS Doubloons, **sharing the
      // Town Builder's economy** - the same balance that buys pool cues and town assets.
      //
      // 🚨 **BUT THIS SERVER DOES NOT HOLD THAT BALANCE.** `docs/CURRENCIES.md` in the Saloon is the
      // one owner: Doubloons live in **MongoDB `player_towns.doubloons`**, keyed by wallet. Revival
      // is a separate .NET service with its own JSON store, and `ProgressionStore.Pay` credits a
      // LOCAL `softCurrency` that nothing else in Vendetta can see.
      //
      // Printing that local number under the word "Doubloons" would show a figure that is NOT the
      // player's doubloon balance, on a screen whose whole job is to tell them the truth about their
      // account - and the two would drift from the first spend. **A currency shared in NAME but not
      // in LEDGER is the two-owners defect wearing a UI.** So nothing is shown until the credit
      // reaches the real ledger.
      //
      // ⚠️ There is a second, harder half: Revival's beta is OPEN and most players are GUESTS with no
      // wallet, and `player_towns` is keyed by one. A guest has no key to credit. That is not a
      // blocker - it is the reason to connect a wallet, and the connect button is right above.
      figure("TIME PLAYED", playtime(seconds)),
      figure("FAVOURITE DECK", str(stats["favDeck"]) || "—",
             str(stats["favDeck"]) ? undefined : "decided once you have played a few"),
    );

    // ── the ladder ───────────────────────────────────────────────────────────────────────────────
    const ladderHead = document.createElement("h3");
    ladderHead.className = "profile__head";
    ladderHead.textContent = "THE LADDER";

    const ladder = document.createElement("div");
    ladder.className = "profile__ladder";

    const byId = new Map<string, string>();
    for (const l of leagues) if (l?.id) byId.set(String(l.id), String(l.leagueName ?? "League"));

    const rows = Array.isArray(stats["leagueStats"])
      ? stats["leagueStats"] as Array<Record<string, unknown>>
      : [];

    if (!rows.length) {
      // ⚠️ The IMPORTED `note()` - it builds an element. `saySo()` sets text on the status line and
      // returns nothing; my blanket rename caught this call too, which the compiler stopped.
      ladder.append(note("No ranked play yet."));
    }
    for (const r of rows) {
      const id = str(r["leagueId"]);
      // ⚠️ A league the lobby no longer offers is SKIPPED rather than shown as its raw id - a GUID
      // on a player's profile is not a fact, it is a leak of our storage.
      const label = byId.get(id);
      if (!label) continue;

      const row = document.createElement("div");
      row.className = "profile__rung";
      const nm = document.createElement("span");
      nm.className = "profile__rungname";
      nm.textContent = label;
      const elo = document.createElement("span");
      elo.className = "profile__elo";
      elo.textContent = String(num(r["elo"]));
      const played = document.createElement("span");
      played.className = "profile__played";
      const g = num(r["games"]);
      played.textContent = g > 0 ? `${num(r["wins"])}W of ${g}` : "unplayed";
      row.append(nm, elo, played);
      ladder.append(row);
    }

    // ── sound lives here too, so it is reachable outside a match ─────────────────────────────────
    const tools = document.createElement("div");
    tools.className = "profile__tools";
    tools.append(settingsButton());

    root.append(who, grid, ladderHead, ladder, tools);
  },
};

function figure(label: string, value: string, hint?: string): HTMLElement {
  const box = document.createElement("div");
  box.className = "profile__figure";
  const v = document.createElement("span");
  v.className = "profile__value";
  v.textContent = value;
  const l = document.createElement("span");
  l.className = "profile__label";
  l.textContent = label;
  box.append(v, l);
  if (hint) {
    const h = document.createElement("span");
    h.className = "profile__hint";
    h.textContent = hint;
    box.append(h);
  }
  return box;
}

/** `1h 20m`, or `—` for nothing. Seconds are meaningless to a player at this scale. */
function playtime(seconds: number): string {
  if (!seconds) return "—";
  const m = Math.round(seconds / 60);
  if (m < 60) return `${m}m`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
}

/**
 * Copy without the clipboard API - insecure origins and denied permissions both land here.
 *
 * ⚠️ `execCommand` is deprecated and still the only thing that works in those cases. The textarea is
 * positioned off-screen rather than `display:none`, because a hidden element cannot be selected and
 * the copy silently does nothing.
 */
function legacyCopy(text: string): void {
  const ta = document.createElement("textarea");
  ta.value = text;
  ta.setAttribute("readonly", "");
  ta.style.position = "fixed";
  ta.style.top = "-1000px";
  document.body.appendChild(ta);
  ta.select();
  try { document.execCommand("copy"); } finally { ta.remove(); }
}

/** `0xec14…6bef` - enough to recognise, short enough to sit on a line. */
function shorten(addr: string): string {
  if (!addr || addr.length < 12) return addr;
  return `${addr.slice(0, 6)}…${addr.slice(-4)}`;
}

function num(v: unknown): number {
  const n = typeof v === "number" ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
}

function str(v: unknown): string {
  return typeof v === "string" ? v : "";
}

function describe(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

// ⚠️ Kept so a future card-based panel (favourite card, most played) has the name resolver already
// imported and the database already warmed; `cardName` is the one owner for a card's title.
void cardName;
